import { createFileRoute } from '@tanstack/react-router';
import { useSignIn, useAuth } from '@clerk/clerk-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { track } from '@/lib/analytics';
import { Button, Card } from '@/components';
import { apiJson } from '@/lib/api';
import { FiShield, FiCheck } from 'react-icons/fi';

type CheckoutSessionResponse = {
  checkout_url: string;
  session_id: string;
};

function PurchasePage() {
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn();
  const { isSignedIn, isLoaded: authLoaded, getToken } = useAuth();
  const { ticket } = Route.useSearch();

  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [error, setError] = useState('');
  
  // Ref to prevent multiple auth attempts
  const authAttemptedRef = useRef(false);

  // Track screen view - differentiate email link clicks vs direct visits
  useEffect(() => {
    if (ticket) {
      // User came from the day-7 email link
      track('Purchase Email Link Clicked');
    }
    track('Purchase Screen', { from_email: !!ticket });
  }, [ticket]);

  // Handle ticket-based sign-in
  useEffect(() => {
    // Guard: don't attempt if already tried, already signed in, or missing requirements
    if (authAttemptedRef.current) return;
    if (!signInLoaded || !signIn || !ticket) return;
    if (isSignedIn) return;

    // Mark as attempted immediately to prevent re-runs
    authAttemptedRef.current = true;

    const authenticateWithTicket = async () => {
      setIsAuthenticating(true);
      setError('');

      try {
        const result = await signIn.create({
          strategy: 'ticket',
          ticket: ticket,
        });

        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          // User will be identified in analytics when they navigate to app.tsx
          track('Purchase Ticket Auth Success');
        } else {
          setError('Sign-in incomplete. Please try again.');
          track('Purchase Ticket Auth Incomplete', { status: result.status });
        }
      } catch (err: unknown) {
        console.error('[Purchase] Ticket auth error:', err);
        const clerkError = err as { errors?: { message?: string }[]; message?: string };
        const message =
          clerkError?.errors?.[0]?.message ??
          clerkError?.message ??
          'Failed to sign in. Please try again.';
        setError(message);
        track('Purchase Ticket Auth Error', { error: message });
      } finally {
        setIsAuthenticating(false);
      }
    };

    authenticateWithTicket();
  }, [signInLoaded, signIn, ticket, setActive, isSignedIn]);

  const handlePurchase = useCallback(async () => {
    if (!authLoaded || !isSignedIn) {
      setError('Please sign in to continue.');
      return;
    }

    track('Purchase Button Tapped');
    setIsCreatingCheckout(true);
    setError('');

    try {
      const response = await apiJson<CheckoutSessionResponse>(
        getToken,
        '/v1/subscription/create-one-time-checkout',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      track('Purchase Checkout Created', { session_id: response.session_id });

      // Redirect to Stripe Checkout
      window.location.href = response.checkout_url;
    } catch (err: unknown) {
      console.error('[Purchase] Checkout error:', err);
      const message = err instanceof Error ? err.message : 'An error occurred during checkout. Please try again.';
      setError(message);
      track('Purchase Checkout Error', { error: message });
      setIsCreatingCheckout(false);
    }
  }, [authLoaded, isSignedIn, getToken]);

  const isLoading = isAuthenticating || !authLoaded || !signInLoaded;
  const canPurchase = authLoaded && isSignedIn && !isLoading;

  return (
    <div className='min-h-screen bg-sky-light'>
      <div className='max-w-2xl mx-auto px-6 py-12'>
        {/* Header */}
        <div className='text-center mb-8'>
          <h1
            className='text-4xl font-semibold text-gray-900 mb-4'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Harbor One-Time Purchase
          </h1>
          <p className='text-lg text-gray-600'>
            Get full access to Harbor's co-parenting tools with a one-time purchase.
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <Card className='text-center py-12'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4' />
            <p className='text-gray-600'>Signing you in...</p>
          </Card>
        )}

        {/* Purchase Content */}
        {!isLoading && (
          <>
            {/* Features */}
            <Card className='mb-6'>
              <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-4'>
                What's included
              </h2>
              <div className='space-y-4'>
                {[
                  'Track and split expenses automatically',
                  'Visual custody calendar with exceptions',
                  'Secure, documented communication',
                  'Court-ready exports in one tap',
                  'Push notifications for updates',
                ].map((feature, index) => (
                  <div key={index} className='flex items-start gap-3'>
                    <FiCheck className='w-5 h-5 text-primary flex-shrink-0 mt-0.5' />
                    <p className='text-gray-700'>{feature}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Important Notice */}
            <Card className='mb-6 bg-blue-50 border-blue-200'>
              <div className='flex items-start gap-3'>
                <FiShield className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5' />
                <div>
                  <h3 className='font-semibold text-blue-900 mb-1'>
                    Web-Only Purchase
                  </h3>
                  <p className='text-sm text-blue-800'>
                    This purchase is processed on Harbor's website (not through Apple
                    App Store or Google Play). Your purchase will unlock access in the
                    Harbor mobile app after completion.
                  </p>
                </div>
              </div>
            </Card>

            {/* Error Message */}
            {error && (
              <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center'>
                {error}
              </div>
            )}

            {/* Purchase Button */}
            <div className='mb-6'>
              <Button
                fullWidth
                onClick={handlePurchase}
                disabled={!canPurchase || isCreatingCheckout}
                loading={isCreatingCheckout}
                size='lg'
                className='shadow-lg shadow-primary/30'
              >
                <FiShield className='w-5 h-5 mr-2' />
                {canPurchase
                  ? 'Complete Purchase'
                  : 'Please wait...'}
              </Button>
            </div>

            {/* Legal */}
            <div className='text-center text-xs text-gray-500'>
              <p className='mb-2'>
                By purchasing, you agree to Harbor's{' '}
                <a
                  href='/terms-of-service'
                  className='text-primary font-semibold hover:underline'
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href='/privacy-policy'
                  className='text-primary font-semibold hover:underline'
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/purchase/')({
  component: PurchasePage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      ticket: (search.ticket as string) || undefined,
    };
  },
});
